<template>
  <div class="activity-list-container">
    <h1 class="page-title">活动管理</h1>
    <div class="content-wrapper">
      <!-- 搜索和筛选区域 -->
      <el-card class="filter-card">
        <div class="filter-row">
          <el-input
            v-model="searchForm.keyword"
            placeholder="搜索活动标题、地点或描述"
            prefix-icon="Search"
            clearable
            style="width: 300px; margin-right: 15px"
          />
          <el-select
            v-model="searchForm.status"
            placeholder="选择活动状态"
            style="width: 150px; margin-right: 15px"
          >
            <el-option label="全部" value="" />
            <el-option label="即将开始" value="upcoming" />
            <el-option label="进行中" value="ongoing" />
            <el-option label="已结束" value="completed" />
          </el-select>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button type="success" @click="handleCreate">
            <el-icon><Plus /></el-icon>
            新增活动
          </el-button>
        </div>
      </el-card>

      <!-- 活动列表 -->
      <el-card class="list-card">
        <el-table
          v-loading="loading"
          :data="activityList"
          stripe
          border
          style="width: 100%"
        >
          <el-table-column prop="title" label="活动标题" min-width="200" />
          <el-table-column prop="location" label="活动地点" />
          <el-table-column prop="start_time" label="开始时间" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.start_time) }}
            </template>
          </el-table-column>
          <el-table-column prop="signup_end_time" label="报名截止" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.signup_end_time) }}
            </template>
          </el-table-column>
          <el-table-column prop="max_participants" label="最大人数" width="100" />
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag
                :type="getStatusTagType(scope.row.status)"
              >
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button
                type="primary"
                size="small"
                @click="handleView(scope.row.id)"
                style="margin-right: 8px"
              >
                <el-icon><View /></el-icon>
                查看
              </el-button>
              <el-button
                type="warning"
                size="small"
                @click="handleEdit(scope.row.id)"
                style="margin-right: 8px"
              >
                <el-icon><Edit /></el-icon>
                编辑
              </el-button>
              <el-button
                type="danger"
                size="small"
                @click="handleDelete(scope.row.id, scope.row.title)"
              >
                <el-icon><Delete /></el-icon>
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
// eslint-disable-next-line no-unused-vars
import { Search, Plus, View, Edit, Delete } from '@element-plus/icons-vue';

import request from '@/api/axios';

const router = useRouter();

// 数据
const loading = ref(false);
const activityList = ref([]);

// 搜索表单
const searchForm = ref({
  keyword: '',
  status: '',
});

// 分页
const pagination = ref({
  page: 1,
  pageSize: 10,
  total: 0,
});

// 获取活动列表
const fetchActivities = async () => {
  loading.value = true;
  try {
    const params = {
      page: pagination.value.page,
      pageSize: pagination.value.pageSize,
      search: searchForm.value.keyword,
      status: searchForm.value.status,
    };

    const response = await request({
      url: '/manage/activities',
      method: 'get',
      params,
    });

    // 确保activityList始终是数组，防止表格组件出错
    activityList.value = Array.isArray(response.data) ? response.data : [];
    pagination.value.total = response.total || 0;
  } catch (error) {
    ElMessage.error('获取活动列表失败：' + (error.message || '未知错误'));
    // 出错时重置为数组
    activityList.value = [];
  } finally {
    loading.value = false;
  }
};

// 搜索
// eslint-disable-next-line no-unused-vars
const handleSearch = () => {
  pagination.value.page = 1;
  fetchActivities();
};

// 新增活动
// eslint-disable-next-line no-unused-vars
const handleCreate = () => {
  router.push('/activities/create');
};

// 查看活动
// eslint-disable-next-line no-unused-vars
const handleView = (id) => {
  router.push(`/activities/${id}`);
};

// 编辑活动
// eslint-disable-next-line no-unused-vars
const handleEdit = (id) => {
  router.push(`/activities/${id}/edit`);
};

// 删除活动
// eslint-disable-next-line no-unused-vars
const handleDelete = (id, title) => {
  ElMessageBox.confirm(`确定要删除活动 "${title}" 吗？`, '警告', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(async () => {
    try {
      await request({
        url: `/manage/activities/${id}`,
        method: 'delete',
      });
      ElMessage.success('删除活动成功');
      fetchActivities();
    } catch (error) {
      ElMessage.error('删除活动失败：' + (error.message || '未知错误'));
    }
  }).catch(() => {
    // 取消删除
  });
};

// 格式化日期
// eslint-disable-next-line no-unused-vars
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// 获取状态文本
// eslint-disable-next-line no-unused-vars
const getStatusText = (status) => {
  const statusMap = {
    upcoming: '即将开始',
    ongoing: '进行中',
    completed: '已结束',
  };
  return statusMap[status] || status;
};

// 获取状态标签类型
// eslint-disable-next-line no-unused-vars
const getStatusTagType = (status) => {
  const typeMap = {
    upcoming: 'success',
    ongoing: 'primary',
    completed: 'info',
  };
  return typeMap[status] || 'default';
};

// 分页大小变化
// eslint-disable-next-line no-unused-vars
const handleSizeChange = (size) => {
  pagination.value.pageSize = size;
  fetchActivities();
};

// 当前页变化
// eslint-disable-next-line no-unused-vars
const handleCurrentChange = (current) => {
  pagination.value.page = current;
  fetchActivities();
};

// 初始化
onMounted(() => {
  fetchActivities();
});
</script>

<style scoped>
.activity-list-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.filter-card {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  align-items: center;
}

.list-card {
  margin-top: 20px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>